// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * race.ts — the netcode, which is deliberately almost nothing.
 *
 * Lastlight's multiplayer is a PARALLEL SAME-SEED RACE. Every peer runs its own
 * `Game` over its own actions, from a seed the host froze into the round start.
 * No peer ever touches another peer's settlement, so there is no shared mutable
 * state, no authority over anyone's simulation, and structurally nothing to
 * desync. What crosses the wire is a status ping for the standings strip and a
 * final result — display data, both of them.
 *
 * That makes host transfer nearly free, and the design leans on it: results are
 * BROADCAST to everyone rather than unicast to the host, precisely so that a
 * promoted peer already holds every result it needs to publish the ladder. The
 * only thing promotion moves is who publishes, never anyone's game.
 *
 * The one thing that can genuinely go wrong here is a room that waits forever
 * on somebody who closed their tab, so the ladder publishes on a visible
 * countdown from the first finisher (principle #12: every waiting state needs a
 * reason, a horizon and an escape), and a peer that leaves is dropped from the
 * expected set rather than being waited on.
 *
 * Timers are setInterval, never rAF: a backgrounded tab freezes rAF, and a
 * countdown that stops when you switch tabs is a room that hangs.
 */

import type { Summary } from './game';

/**
 * The slice of `Net` this module needs. Structural, so a test can drive a whole
 * race with a two-line fake bus and no WebRTC anywhere near it.
 */
export interface RaceNet {
  readonly selfId: string;
  channel<T>(
    name: string,
    onReceive: (data: T, from: string) => void,
  ): ((data: T, toPeers?: string | string[]) => void) & { off: () => void };
}

/** How long the room holds the ladder open for stragglers after the first finish. */
export const LADDER_GRACE_MS = 25_000;
const TICK_MS = 250;

export interface RaceSeat {
  id: string;
  name: string;
}

/** Live status of one settlement, as of its last ping. */
export interface Standing {
  id: string;
  name: string;
  isSelf: boolean;
  day: number;
  pop: number;
  score: number;
  /** Their run has ended — won or lost. */
  done: boolean;
  /** Present once they have reported a final result. */
  result: Summary | null;
  /** They left the room before finishing. */
  gone: boolean;
}

interface StatusMsg {
  r: number;
  day: number;
  pop: number;
  score: number;
}

interface FinMsg {
  r: number;
  score: number;
  survivors: number;
  buildings: number;
  caches: number;
  days: number;
  won: boolean;
  reason: string;
}

interface LadderMsg {
  r: number;
  /** Ordered ids, best first. */
  order: string[];
}

export interface RaceConfig {
  /** Omit for a solo run — every method then works and sends nothing. */
  net?: RaceNet | null;
  /** The rematch round number. Anything from another round is ignored. */
  round: number;
  /** The frozen roster for this round. Index N is player N on every peer. */
  seats: RaceSeat[];
  selfId: string;
  isHost: boolean;
  graceMs?: number;
  /** Standings changed; repaint the strip. */
  onChange?: () => void;
  /** The ladder is final. Show the results screen. */
  onLadder?: () => void;
}

export interface Race {
  standings(): Standing[];
  /** Broadcast where this settlement is up to. Cheap; call it on every day roll. */
  ping(day: number, pop: number, score: number): void;
  /** Report this settlement's final result. Idempotent. */
  finish(summary: Summary): void;
  /** True once the ladder is settled and the results are final. */
  settled(): boolean;
  /** Ms until the ladder publishes without the stragglers, or null. */
  closesInMs(): number | null;
  /**
   * Promotion / demotion, wired from `createNet`'s `onHostChange`. Network-free
   * on purpose so it is unit-testable without a mesh: the promoted peer already
   * holds every result (they were broadcast, not unicast), so taking over is
   * "resume the countdown and publish when it expires".
   */
  setHost(isHost: boolean): void;
  isHost(): boolean;
  /** A peer dropped. Stop waiting on them so the ladder can still close. */
  peerLeft(id: string): void;
  /** Host only: publish the ladder now, without the stragglers. */
  closeNow(): void;
  destroy(): void;
}

export function createRace(cfg: RaceConfig): Race {
  const net = cfg.net ?? null;
  const round = cfg.round;
  const graceMs = cfg.graceMs ?? LADDER_GRACE_MS;

  let host = cfg.isHost;
  let published = false;
  let destroyed = false;
  let deadline: number | null = null;
  let elapsed = 0;
  let timer: ReturnType<typeof setInterval> | undefined;
  let order: string[] | null = null;

  const seats = new Map<string, Standing>();
  for (const s of cfg.seats) {
    seats.set(s.id, {
      id: s.id,
      name: s.name,
      isSelf: s.id === cfg.selfId,
      day: 1,
      pop: 0,
      score: 0,
      done: false,
      result: null,
      gone: false,
    });
  }

  const changed = (): void => cfg.onChange?.();

  // ── wire ──────────────────────────────────────────────────────────────────

  const sendSt = net?.channel<StatusMsg>('st', (msg, from) => {
    if (destroyed || msg.r !== round) return;
    const s = seats.get(from);
    if (!s || s.done) return;
    s.day = msg.day;
    s.pop = msg.pop;
    s.score = msg.score;
    changed();
  });

  const sendFin = net?.channel<FinMsg>('fin', (msg, from) => {
    if (destroyed || msg.r !== round) return;
    record(from, msg);
  });

  const sendLad = net?.channel<LadderMsg>('lad', (msg) => {
    if (destroyed || msg.r !== round || published) return;
    // The host has called it. Adopt its ordering rather than recomputing, so
    // every peer shows the same ladder even if one of them is missing a result.
    order = msg.order;
    publishLocally();
  });

  function record(id: string, msg: FinMsg): void {
    const s = seats.get(id);
    if (!s || s.result) return;
    s.done = true;
    s.day = msg.days;
    s.score = msg.score;
    s.pop = msg.survivors;
    s.result = {
      score: msg.score,
      survivors: msg.survivors,
      buildings: msg.buildings,
      caches: msg.caches,
      days: msg.days,
      won: msg.won,
      reason: msg.reason,
    };
    changed();
    openWindow();
    considerClosing();
  }

  // ── closing the ladder ────────────────────────────────────────────────────

  /** Everyone still in the room who is expected to report. */
  function expected(): Standing[] {
    return [...seats.values()].filter((s) => !s.gone);
  }

  function allIn(): boolean {
    const exp = expected();
    return exp.length > 0 && exp.every((s) => s.result !== null);
  }

  /** The first finish starts the clock the rest of the room is running against. */
  function openWindow(): void {
    if (published || deadline !== null) return;
    if (![...seats.values()].some((s) => s.result)) return;
    deadline = graceMs;
    elapsed = 0;
    ensureTimer();
    changed();
  }

  function ensureTimer(): void {
    if (timer || destroyed || published) return;
    timer = setInterval(tick, TICK_MS);
  }

  function stopTimer(): void {
    if (timer) clearInterval(timer);
    timer = undefined;
  }

  function tick(): void {
    if (destroyed || published || deadline === null) return;
    elapsed += TICK_MS;
    if (elapsed >= deadline) {
      // Only the host closes. If every peer did, two ladders could disagree
      // about who was still out there.
      if (host) closeNow();
      else changed();
      return;
    }
    changed();
  }

  function considerClosing(): void {
    if (published || !host) return;
    if (allIn()) closeNow();
  }

  function rank(): string[] {
    // Finished settlements first, by score; a tie goes to whoever held out more
    // days, then to a stable id so every peer sorts identically.
    return [...seats.values()]
      .sort((a, b) => {
        const ar = a.result;
        const br = b.result;
        if (!!ar !== !!br) return ar ? -1 : 1;
        if (ar && br) {
          if (br.score !== ar.score) return br.score - ar.score;
          if (br.days !== ar.days) return br.days - ar.days;
        }
        return a.id < b.id ? -1 : 1;
      })
      .map((s) => s.id);
  }

  function publishLocally(): void {
    if (published) return;
    published = true;
    stopTimer();
    deadline = null;
    changed();
    cfg.onLadder?.();
  }

  /** Host only: settle the ladder and tell the room. */
  function closeNow(): void {
    if (published || destroyed) return;
    order = rank();
    sendLad?.({ r: round, order });
    publishLocally();
  }

  return {
    standings(): Standing[] {
      const list = [...seats.values()];
      if (order) {
        const at = new Map(order.map((id, i) => [id, i]));
        list.sort((a, b) => (at.get(a.id) ?? 99) - (at.get(b.id) ?? 99));
      } else {
        list.sort((a, b) => b.score - a.score || (a.id < b.id ? -1 : 1));
      }
      return list;
    },

    ping(day, pop, score): void {
      if (destroyed) return;
      const me = seats.get(cfg.selfId);
      if (me && !me.done) {
        me.day = day;
        me.pop = pop;
        me.score = score;
        changed();
      }
      sendSt?.({ r: round, day, pop, score });
    },

    finish(summary): void {
      if (destroyed) return;
      const msg: FinMsg = {
        r: round,
        score: summary.score,
        survivors: summary.survivors,
        buildings: summary.buildings,
        caches: summary.caches,
        days: summary.days,
        won: summary.won,
        reason: summary.reason,
      };
      record(cfg.selfId, msg);
      // Broadcast, NOT unicast to the host: this is what makes a promoted peer
      // able to publish a ladder it never asked for.
      sendFin?.(msg);
      // Solo, or last one standing — nothing to wait for.
      if (!net) publishLocally();
    },

    settled: () => published,
    closesInMs: () => (deadline === null || published ? null : Math.max(0, deadline - elapsed)),

    setHost(isHost: boolean): void {
      host = isHost;
      if (!host) return;
      // Adopt the room's outstanding business: if results are already in and
      // nobody has closed, this peer now owns closing it.
      if (!published) {
        if (allIn()) return closeNow();
        if (deadline !== null) ensureTimer();
        else openWindow();
      }
      changed();
    },

    isHost: () => host,

    peerLeft(id: string): void {
      const s = seats.get(id);
      if (!s || s.gone) return;
      s.gone = true;
      changed();
      // A room must never hang on somebody who is not coming back.
      considerClosing();
    },

    closeNow,

    destroy(): void {
      destroyed = true;
      stopTimer();
      sendSt?.off();
      sendFin?.off();
      sendLad?.off();
    },
  };
}
